import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { jsPDF } from 'jspdf';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { checkSubscription } from '../subscriptionUtils';
import logo from '../assets/logo.png';
import './AppContent.css';
import { ClipLoader } from 'react-spinners';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { v4 as uuidv4 } from 'uuid';
import { getBackendUrl } from '../apiConfig';

const AppContent = ({ user }) => {
    const [extractedDataVisible, setExtractedDataVisible] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [extractedData, setExtractedData] = useState([]);
    const [classification, setClassification] = useState(null);
    const [description, setDescription] = useState('');
    const [material, setMaterial] = useState('');
    const [intendedUse, setIntendedUse] = useState('');
    const [casNumber, setCasNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [isProUser, setIsProUser] = useState(false);
    const [usage, setUsage] = useState(null);
    const navigate = useNavigate();
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);

    const backendUrl = getBackendUrl();

    useEffect(() => {
        const verifySubscription = async () => {
            const { isAuthenticated, planLevel } = await checkSubscription();
            if (!isAuthenticated) {
                navigate('/login');
                return;
            }
            setIsProUser(planLevel === 'Pro');
        };
        verifySubscription();
    }, [navigate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                console.log('Click outside detected, closing dropdown');
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.docx'],
            'text/plain': ['.txt'],
            'image/png': ['.png'],
            'image/jpeg': ['.jpeg', '.jpg'],
        },
        onDrop: async (acceptedFiles, fileRejections) => {
            setUploadedFiles(acceptedFiles);
            if (fileRejections.length > 0) {
                const rejectionMessages = fileRejections.map(rejection =>
                    `${rejection.file.name}: ${rejection.errors[0].message}`
                ).join(', ');
                toast.error(`Some files were rejected: ${rejectionMessages}`);
            }
            if (acceptedFiles.length === 0) return;
            setLoading(true);
            const formData = new FormData();
            acceptedFiles.forEach(file => formData.append('files', file));
            formData.append('description', description);
            formData.append('casNumber', casNumber);

            try {
                const response = await fetch(`${backendUrl}/upload`, {
                    method: 'POST',
                    body: formData,
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Upload failed: ${errorText || 'Server error'}`);
                }
                const data = await response.json();
                setExtractedData(data.extractedData);
                setExtractedDataVisible(true);
                toast.success('Files uploaded successfully!');
            } catch (error) {
                console.error('Upload error:', error);
                toast.error(`Upload failed: ${error.message || 'Please try again or contact support.'}`);
            } finally {
                setLoading(false);
            }
        },
    });

    const handleClassify = async () => {
        if (!description.trim() && extractedData.length === 0 && !casNumber.trim()) {
            toast.error('Please enter a product description, upload documents, or provide a CAS Number for classification.');
            return;
        }

        setLoading(true);
        const classificationId = uuidv4();
        const userId = auth.currentUser?.uid;
        if (!userId) {
            toast.error('User not authenticated. Please log in again.');
            setLoading(false);
            navigate('/login');
            return;
        }

        // Subscription check
        const { hasActiveSubscription } = await checkSubscription();
        if (!hasActiveSubscription) {
            toast.error(
                <>
                    Active or trialing subscription required to classify goods.{' '}
                    <a href="/subscription/manage">Manage your subscription</a>.
        </>,
                { autoClose: 5000 }
            );
            setLoading(false);
            navigate('/subscription');
            return;
        }

        const payload = { userId, classificationId, description, extractedData, material, intendedUse, casNumber };

    // Custom fetch with timeout
    const fetchWithTimeout = async (url, options, timeout = 45000) => {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        try {
            const response = await fetch(url, { ...options, signal: controller.signal });
            clearTimeout(id);
            return response;
        } catch (error) {
            clearTimeout(id);
            throw error;
        }
    };

        try {
            let hsData = null;
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    toast.info(`Fetching initial HS code (attempt ${attempt}/3)...`, { autoClose: false, toastId: 'hs' });
                    const response = await fetchWithTimeout(`${backendUrl}/classify/hs`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    }, 30000);
                    if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.error || `Initial HS classification failed: ${response.status}`);
                    }
                    hsData = await response.json();
                    toast.update('hs', { render: 'Initial HS code retrieved', type: 'success', autoClose: 2000 });
                    break;
                } catch (error) {
                    console.error(`Initial HS attempt ${attempt} failed:`, error);
                    if (attempt === 3 || error.name === 'AbortError') {
                        toast.update('hs', { render: `Failed to fetch initial HS code: ${error.message}`, type: 'error', autoClose: 5000 });
                        throw error;
                    }
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                }
            }

            if (hsData.hsCode === 'Unknown' || hsData.error) {
                toast.error(hsData.error || 'Initial HS code classification failed', { autoClose: 5000 });
                throw new Error('Invalid initial HS code');
            }

            let refineData = null;
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    toast.info(`Refining HS code (attempt ${attempt}/3)...`, { autoClose: false, toastId: 'refine' });
                    const response = await fetchWithTimeout(`${backendUrl}/classify/refine`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ...payload, initialHsCode: hsData.hsCode }),
                    }, 30000);
                    if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.error || `Refine HS classification failed: ${response.status}`);
                    }
                    refineData = await response.json();
                    toast.update('refine', { render: 'HS code refined', type: 'success', autoClose: 2000 });
                    break;
                } catch (error) {
                    console.error(`Refine HS attempt ${attempt} failed:`, error);
                    if (attempt === 3 || error.name === 'AbortError') {
                        toast.update('refine', { render: `Failed to refine HS code: ${error.message}`, type: 'error', autoClose: 5000 });
                        throw error;
                    }
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                }
            }

            if (refineData.hsCode === 'Unknown' || refineData.error) {
                toast.error(refineData.error || 'HS code refinement failed', { autoClose: 5000 });
                throw new Error('Invalid refined HS code');
            }

            let tcoGSTData = null;
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    toast.info(`Checking TCO and GST exemptions (attempt ${attempt}/3)...`, { autoClose: false, toastId: 'tcoGST' });
                    const response = await fetchWithTimeout(`${backendUrl}/classify/tcoGST`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            ...payload,
                            hsCode: refineData.hsCode,
                            dutyRate: refineData.dutyRate
                        }),
                    }, 45000); // Increased timeout for TCO/GST
                    if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.error || `TCO/GST classification failed: ${response.status}`);
                    }
                    tcoGSTData = await response.json();
                    toast.update('tcoGST', { render: 'TCO and GST exemptions checked', type: 'success', autoClose: 2000 });
                    break;
                } catch (error) {
                    console.error(`TCO/GST attempt ${attempt} failed:`, error);
                    if (attempt === 3 || error.name === 'AbortError') {
                        toast.update('tcoGST', { render: `Failed to check TCO/GST: ${error.message}`, type: 'error', autoClose: 5000 });
                        throw error;
                    }
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                }
            }

            if (tcoGSTData.error) {
                toast.error(tcoGSTData.error, { autoClose: 5000 });
                throw new Error(tcoGSTData.error);
            }

            const finalClassification = {
                hsCode: refineData.hsCode,
                confidence: hsData.confidence,
                statCode: refineData.statCode,
                aheccCode: refineData.aheccCode,
                tcoSuggestions: tcoGSTData.tcoSuggestions,
                explanation: refineData.explanation,
                dutyRate: refineData.dutyRate,
                gstExemption: tcoGSTData.gstExemption,
                unit: refineData.unit
            };

            console.log('Classification Data:', finalClassification);
            setClassification(finalClassification);
            setUsage({
                classificationsUsed: tcoGSTData.classificationsUsed || 0,
                classificationLimit: tcoGSTData.classificationLimit || 150
            });
            toast.success('Classification completed successfully!', { autoClose: 3000 });
        } catch (error) {
            console.error('Classification failed:', error);
            toast.error(`Classification failed: ${error.message}`, { autoClose: 5000 });
        } finally {
            setLoading(false);
        }
    };

    const saveClassification = () => {
        if (!isProUser) {
            toast.error('Saving classifications is a Pro feature. Upgrade your plan to access History.', { autoClose: 5000 });
            return;
        }

        if (!classification) {
            toast.error('No classification data to save!');
            return;
        }

        const technicalDescription = extractedData.length > 0
            ? extractedData
                .map(data => data.technicalData)
                .filter(text => text && text.trim() && !text.startsWith('Error'))
                .join(' ')
            : '';
        const effectiveDescription = [description, technicalDescription, casNumber ? `CAS: ${casNumber}` : '']
            .filter(text => text && text.trim())
            .join(' ');

        const savedData = {
            description: effectiveDescription || 'No description provided',
            material,
            intendedUse,
            casNumber,
            classification,
            timestamp: new Date().toLocaleString('en-AU', { timeZone: 'Australia/Victoria' }),
        };

        try {
            const historyData = JSON.parse(localStorage.getItem('customspro_classifications') || '{}');
            const existing = Array.isArray(historyData.classifications) ? [...historyData.classifications] : [];
            existing.push(savedData);
            const updatedData = {
                classifications: existing,
                expiry: historyData.expiry || Date.now() + 7 * 24 * 60 * 60 * 1000,
            };
            localStorage.setItem('customspro_classifications', JSON.stringify(updatedData));

            toast.success('Classification saved successfully!');
            console.log('Saved Classifications:', existing);
        } catch (error) {
            console.error('Error saving classification:', error);
            toast.error('Failed to save classification. Please try again.');
        }
    };

    const clearAllData = () => {
        setDescription('');
        setMaterial('');
        setIntendedUse('');
        setCasNumber('');
        setUploadedFiles([]);
        setExtractedData([]);
        setExtractedDataVisible(false);
        setClassification(null);
        setLoading(false);
        setUsage(null);
        toast.info('All data cleared.');
    };

    const loadImageAsBase64 = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                // Limit canvas size to reduce PDF file size
                const maxDimension = 300;
                let { width, height } = img;
                if (width > height) {
                    if (width > maxDimension) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    }
                } else {
                    if (height > maxDimension) {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                // JPEG has no alpha — fill white so rounded logo corners aren't black
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                const dataURL = canvas.toDataURL('image/jpeg', 0.7); // Use JPEG with 70% quality
                resolve({
                    dataURL,
                    width,
                    height,
                });
            };
            img.onerror = (error) => reject(error);
        });
    };

    const exportToPDF = async () => {
        if (!classification) {
            toast.error('No classification data to export!');
            return;
        }
    
        const technicalDescription = extractedData.length > 0
            ? extractedData
                .map(data => data.technicalData)
                .filter(text => text && text.trim() && !text.startsWith('Error'))
                .join(' ')
            : '';
        const effectiveDescription = [description, technicalDescription, casNumber ? `CAS: ${casNumber}` : '']
            .filter(text => text && text.trim())
            .join(' ');
    
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const maxWidth = pageWidth - 2 * margin;
        let yPos = margin;
    
        // Helper function to set body text style
        const setBodyStyle = () => {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
        };
    
        // Helper function to check for page overflow and add new page if needed
        const checkPageOverflow = (requiredHeight) => {
            if (yPos + requiredHeight > pageHeight - margin) {
                doc.addPage();
                yPos = margin; // Reset yPos to top of new page
                // Add header on new pages
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(16);
                doc.setTextColor(0, 123, 255);
                doc.text('Customs Pro AI Classification Report (Continued)', margin, yPos);
                yPos += 10;
                doc.setLineWidth(0.5);
                doc.setDrawColor(0, 123, 255);
                doc.line(margin, yPos, pageWidth - margin, yPos);
                yPos += 10;
                setBodyStyle(); // Reset to body style for subsequent content
            }
        };
    
        // Initial header
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(16);
        doc.setTextColor(0, 123, 255);
        doc.text('Customs Pro AI Classification Report', margin, yPos);
        yPos += 10;
    
        let calculatedHeight = 0;
        try {
            const { dataURL: logoBase64, width: logoWidth, height: logoHeight } = await loadImageAsBase64(logo);
            const desiredWidth = 30;
            const aspectRatio = logoWidth / logoHeight;
            calculatedHeight = desiredWidth / aspectRatio;
            doc.addImage(logoBase64, 'JPEG', pageWidth - margin - desiredWidth, margin - 5, desiredWidth, calculatedHeight);
        } catch (error) {
            console.error('Logo load failed:', error);
        }
    
        const logoBottom = (margin - 5) + calculatedHeight;
        yPos = Math.max(yPos, logoBottom + 5);
        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 123, 255);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
    
        setBodyStyle();
        doc.setFont('helvetica', 'bold');
        const timestamp = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Victoria' });
        doc.text(`Classification - ${timestamp}`, margin, yPos);
        yPos += 5;
    
        // Product Description
        setBodyStyle();
        const descLines = doc.splitTextToSize(effectiveDescription || 'No description provided.', maxWidth);
        descLines.forEach((line) => {
            checkPageOverflow(5); // Check for each line (5mm height per line)
            setBodyStyle();
            doc.text(line, margin, yPos);
            yPos += 5;
        });
        yPos += 5;
    
        // Classification Fields
        const fields = [
            { label: 'HS Code', value: classification.hsCode + (classification.confidence ? ` (${classification.confidence})` : '') },
            { label: 'Stat Code', value: classification.statCode },
            { label: 'AHECC', value: classification.aheccCode },
            { label: 'Duty Rate', value: classification.dutyRate },
            { label: 'Unit', value: classification.unit },
            { label: 'GST Exemption', value: classification.gstExemption },
        ];
    
        fields.forEach(({ label, value }) => {
            checkPageOverflow(5); // Check for each field line
            doc.setFont('helvetica', 'bold');
            doc.text(`${label}:`, margin, yPos);
            setBodyStyle(); // Reset to body style for value
            doc.text(value || 'Unknown', margin + 30, yPos);
            yPos += 5;
        });
    
        yPos += 5;
        checkPageOverflow(10); // Check for TCO Suggestions header
        doc.setFont('helvetica', 'bold');
        doc.text('TCO Suggestions:', margin, yPos);
        yPos += 5;
        setBodyStyle();
        const tcoText = classification.tcoSuggestions
            ? classification.tcoSuggestions.map(t => t.tco === 'None' ? 'No TCOs applicable' : `${t.tco} - ${t.desc}`).join(', ')
            : 'Not classified yet';
        const tcoLines = doc.splitTextToSize(tcoText, maxWidth - 30);
        tcoLines.forEach((line) => {
            checkPageOverflow(5); // Check for each TCO line
            setBodyStyle();
            doc.text(line, margin + 5, yPos);
            yPos += 5;
        });
    
        yPos += 5;
        checkPageOverflow(10); // Check for Explanation header
        doc.setFont('helvetica', 'bold');
        doc.text('Explanation:', margin, yPos);
        yPos += 5;
        setBodyStyle();
        const explanationLines = doc.splitTextToSize(classification.explanation || 'No explanation available.', maxWidth);
        explanationLines.forEach((line) => {
            checkPageOverflow(5); // Check for each explanation line
            setBodyStyle();
            doc.text(line, margin, yPos);
            yPos += 5;
        });
    
        // Footer (only on the last page)
        checkPageOverflow(10); // Ensure footer fits
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Generated on: ${timestamp}`, margin, pageHeight - 10);
    
        const filename = `Classification_${classification.hsCode || 'Unknown'}_${Date.now()}.pdf`;
        doc.save(filename);
        toast.success('PDF exported successfully!');
    };

    const handleLogout = () => {
        signOut(auth).then(() => {
            navigate('/');
        }).catch((error) => {
            console.error('Logout failed:', error);
            toast.error('Logout failed. Please try again.');
        });
    };

    return (
        <div className="app-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover />
            {loading && (
                <div className="loading-overlay">
                    <ClipLoader color="#007bff" size={50} aria-label="Loading, please wait" />
                    <span className="loading-text">Processing...</span>
                </div>
            )}
            <header className="app-header">
                <img src={logo} alt="Customs Pro AI Logo" className="app-logo" />
                <h1>Customs Pro AI</h1>
                <nav className="app-nav">
                    <Link to="/" className="nav-item">Home</Link>
                    <span className="nav-item active">Classify Goods</span>
                    <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                    <div
                        className={`nav-item pro-dropdown ${!isProUser ? 'disabled' : ''} ${isProDropdownOpen ? 'open' : ''}`}
                        title={!isProUser ? 'Pro level users only, please upgrade your subscription' : ''}
                        onClick={() => {
                            if (!isProUser) return;
                            console.log('Toggling dropdown, new state:', !isProDropdownOpen);
                            setIsProDropdownOpen(!isProDropdownOpen);
                        }}
                    >
                        <span>Pro</span>
                        <div className="dropdown-content" onClick={(e) => e.stopPropagation()}>
                            <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                            <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                            <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                            <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                            <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                            <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
                            <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                            <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                            <Link to="/quarantine-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                            <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                        </div>
                    </div>
                    <Link to="/settings" className="nav-item">Settings</Link>
                    <span
                        className="nav-item logout"
                        onClick={handleLogout}
                        onKeyDown={(e) => e.key === 'Enter' && handleLogout()}
                        role="button"
                        tabIndex={0}
                        aria-label="Log out of the application"
                    >
                        Logout
                    </span>
                </nav>
            </header>

            <main className="app-main">
                <section className="left-panel">
                    <h2 className="panel-title">Classify Goods</h2>
                    <label className="input-label">Product Description</label>
                    <textarea
                        className="input-textarea"
                        placeholder="e.g., Stainless steel bolts, 10mm, for machinery"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        aria-label="Enter product description"
                    />

                    <label className="input-label" htmlFor="file-upload">Upload Documents</label>
                    <div
                        {...getRootProps()}
                        className="drop-area"
                        role="button"
                        aria-describedby="format-note"
                    >
                        <input
                            {...getInputProps({
                                id: 'file-upload',
                                'aria-label': 'Drag files here or click to browse for documents',
                                'aria-describedby': 'format-note',
                            })}
                        />
                        {loading ? 'Processing...' : uploadedFiles.length > 0
                            ? `Uploaded: ${uploadedFiles.map(file => file.name).join(', ')}`
                            : 'Drag files or click to browse'}
                    </div>
                    <p id="format-note" className="format-note">
                        Supported formats: PDF, DOCX, TXT, PNG, JPEG (images for OCR or goods photos)
                    </p>

                    <div className="button-group">
                        <button
                            className="action-btn btn primary-btn extract-btn"
                            onClick={() => setExtractedDataVisible(!extractedDataVisible)}
                            disabled={loading || !user}
                            aria-label={extractedDataVisible ? 'Hide extracted data' : 'Show extracted data'}
                        >
                            {extractedDataVisible ? 'Hide Data' : 'Extract Data'}
                        </button>
                        <button
                            className="action-btn btn secondary-btn clear-btn"
                            onClick={() => {
                                setUploadedFiles([]);
                                setExtractedData([]);
                                setExtractedDataVisible(false);
                                toast.info('Uploaded files cleared.');
                            }}
                            disabled={loading || !user}
                            aria-label="Clear uploaded files"
                        >
                            Clear Uploads
                        </button>
                    </div>

                    {extractedDataVisible && (
                        <div className="extracted-section">
                            <h3 className="extracted-title">Extracted Data</h3>
                            {extractedData.map((data, index) => (
                                <div key={index} className="extracted-item">
                                    <label className="input-label">Technical Data:</label>
                                    <textarea
                                        className="extracted-input"
                                        defaultValue={data.technicalData}
                                        aria-label={`Technical data ${index + 1}`}
                                        readOnly
                                    />
                                    <label className="input-label">IDM Insights:</label>
                                    <input
                                        className="extracted-input"
                                        defaultValue={data.idmInsights}
                                        aria-label={`IDM insights ${index + 1}`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="optional-inputs">
                        <div className="input-group">
                            <label className="input-label">Material</label>
                            <input
                                className="input-field"
                                placeholder="e.g., Steel"
                                value={material}
                                onChange={(e) => setMaterial(e.target.value)}
                                aria-label="Enter material of the product"
                            />
                        </div>
                        <div className="input-group">
                            <label className="input-label">Intended Use</label>
                            <input
                                className="input-field"
                                placeholder="e.g., Construction"
                                value={intendedUse}
                                onChange={(e) => setIntendedUse(e.target.value)}
                                aria-label="Enter intended use of the product"
                            />
                        </div>
                        <div className="input-group">
                            <label className="input-label">CAS Number</label>
                            <input
                                className="input-field"
                                placeholder="e.g., 7647-14-5"
                                value={casNumber}
                                onChange={(e) => setCasNumber(e.target.value)}
                                aria-label="Enter CAS Number of the chemical"
                            />
                        </div>
                    </div>

                    <button
                        className="action-btn btn primary-btn classify-btn"
                        onClick={handleClassify}
                        disabled={loading || !user}
                        aria-label="Classify the uploaded goods based on description, documents, and CAS Number"
                    >
                        {loading ? 'Classifying...' : 'Classify Now'}
                    </button>

                    {usage && (
                        <>
                            <h3 className="section-title">Usage</h3>
                            <div className="result-row">
                                <div className="result-column">
                                    <label className="input-label">Classifications Used</label>
                                    <div className="result-value">
                                        {usage.classificationsUsed} / {usage.classificationLimit}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </section>

                <section className="right-panel">
                    <h2 className="panel-title">Classification Results</h2>

                    <h3 className="section-title">Imports</h3>
                    <div className="result-row">
                        <div className="result-column">
                            <label className="input-label">HS Code</label>
                            <div className="result-value">
                                {classification?.hsCode || 'Not classified yet'}{' '}
                                {classification?.confidence && <span>({classification.confidence})</span>}
                            </div>
                        </div>
                        <div className="result-column">
                            <label className="input-label">Stat Code</label>
                            <div className="result-value">
                                {classification?.statCode || 'Not classified yet'}
                            </div>
                        </div>
                        <div className="result-column">
                            <label className="input-label">Unit</label>
                            <div className="result-value">
                                {classification?.unit || 'Unknown'}
                            </div>
                        </div>
                    </div>
                    <div className="result-row">
                        <div className="result-column">
                            <label className="input-label">Duty Rate</label>
                            <div className="result-value">
                                {classification?.dutyRate || 'Unknown'}
                            </div>
                        </div>
                        <div className="result-column">
                            <label className="input-label">GST Exemption</label>
                            <div className="result-value">
                                {classification?.gstExemption || 'NONE'}
                            </div>
                        </div>
                    </div>
                    <label className="input-label">TCO Suggestions</label>
                    <div className="tco-list">
                        {classification?.tcoSuggestions?.length > 0 ? (
                            classification.tcoSuggestions.map((tcoItem, index) => (
                                <div key={index} className="tco-item">
                                    {tcoItem.tco === 'None' ? (
                                        'No TCOs applicable'
                                    ) : tcoItem.tco === 'Error' ? (
                                        `Error: ${tcoItem.desc}`
                                    ) : (
                                        `${tcoItem.tco} - ${tcoItem.desc}`
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="tco-item">Not classified yet</div>
                        )}
                    </div>

                    <h3 className="section-title">Exports</h3>
                    <div className="result-row">
                        <div className="result-column">
                            <label className="input-label">AHECC</label>
                            <div className="result-value">
                                {classification?.aheccCode || 'Not classified yet'}
                            </div>
                        </div>
                    </div>

                    <label className="input-label">Explanation</label>
                    <div className="explanation-box">
                        {classification?.explanation || 'No explanation available.'}
                    </div>

                    <div className="button-wrapper">
                        <div className="action-group">
                            <button
                                className="action-btn btn primary-btn save-btn"
                                onClick={saveClassification}
                                disabled={loading || !isProUser || !user}
                                title={!isProUser ? 'Pro level users only, please upgrade your subscription' : ''}
                                aria-label="Save the current classification results"
                            >
                                Save Classification
                            </button>
                            <button
                                className="action-btn btn secondary-btn export-btn"
                                onClick={exportToPDF}
                                disabled={loading || !user}
                                aria-label="Export classification results as a PDF"
                            >
                                Export as PDF
                            </button>
                            <button
                                className="action-btn btn secondary-btn clear-data-btn"
                                onClick={clearAllData}
                                disabled={loading || !user}
                                aria-label="Clear all input data and classification results"
                            >
                                Clear Data
                            </button>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default AppContent;